/**
 * SegnalazioneForm Component - Hedita Design System
 * Feature: Segnalazioni - Form creazione/modifica
 */

import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, CheckCircle, AlertTriangle, Building2, Tag, FileText, Save } from "lucide-react";
import { useSegnalazione, useCreateSegnalazione, useUpdateSegnalazione } from "../hooks";
import { useStrutture } from "@/features/strutture";
import { useQueryClient } from "@tanstack/react-query";

const TIPI = ["Manutenzione", "Pulizia", "Servizi", "Sicurezza", "Altro"];
const PRIORITA = ["bassa", "normale", "alta", "urgente"];
const ACCENT = "var(--primary)";

const empty = {
  titolo: "",
  descrizione: "",
  tipo: "Manutenzione",
  priorita: "normale",
  struttura_id: "",
  segnalato_da: "",
};

function Section({ icon: Icon, title, children }: { icon: any; title: string; children: React.ReactNode }) {
  return (
    <div className="bg-card rounded-2xl border border-border overflow-hidden">
      <div className="flex items-center gap-2.5 px-5 py-3.5 border-b border-border bg-muted/10">
        <Icon className="h-4 w-4 flex-shrink-0" style={{ color: ACCENT }} />
        <span className="text-sm font-semibold text-foreground">{title}</span>
      </div>
      <div className="p-5 space-y-4">{children}</div>
    </div>
  );
}

interface SegnalazioneFormProps {
  id?: string;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  onSaved?: () => void;
}

export function SegnalazioneForm({ id, onSaved }: SegnalazioneFormProps) {
  const isNew = !id || id === "nuovo";
  const navigate = useNavigate();
  
  const { data: segnalazione, isLoading } = useSegnalazione(isNew ? undefined : id);
  const { data: strutture = [] } = useStrutture();
  const createMut = useCreateSegnalazione();
  const updateMut = useUpdateSegnalazione();

  const [form, setForm] = useState(empty);
  const [saveStatus, setSaveStatus] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    if (segnalazione) {
      setForm({
        titolo: segnalazione.titolo || "",
        descrizione: segnalazione.descrizione || "",
        tipo: segnalazione.tipo || "Manutenzione",
        priorita: segnalazione.priorita || "normale",
        struttura_id: segnalazione.struttura_id || "",
        segnalato_da: segnalazione.segnalato_da || "",
      });
    }
  }, [segnalazione]);

  const set = (k: keyof typeof form, v: string) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!form.titolo.trim()) return;
    
    setSaveStatus("saving");
    setSaveError(null);

    try {
      if (isNew) {
        await createMut.mutateAsync(form);
      } else {
        await updateMut.mutateAsync({ id: id!, data: form });
      }
      
      setSaveStatus("saved");
      setTimeout(() => {
        setSaveStatus("idle");
        onSaved?.();
        navigate("/segnalazioni");
      }, 1000);
    } catch (error) {
      setSaveStatus("error");
      setSaveError(error instanceof Error ? error.message : "Errore durante il salvataggio");
    }
  };

  const isLoadingData = isLoading && !isNew;

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="border-b border-border bg-card">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate("/segnalazioni")}
                className="p-2 hover:bg-muted rounded-lg transition-colors"
              >
                <ArrowLeft className="h-5 w-5" />
              </button>
              <div>
                <h1 className="text-xl font-semibold text-foreground flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-orange-500" />
                  {isNew ? "Nuova Segnalazione" : "Modifica Segnalazione"}
                </h1>
                <p className="text-sm text-muted-foreground">
                  {isNew ? "Crea una nuova segnalazione" : "Modifica la segnalazione esistente"}
                </p>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate("/segnalazioni")}
                className="px-4 py-2 text-muted-foreground hover:text-foreground transition-colors"
              >
                Annulla
              </button>
              <button
                onClick={handleSave}
                disabled={!form.titolo.trim() || saveStatus === "saving"}
                className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-xl hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {saveStatus === "saving" ? (
                  <>
                    <div className="h-4 w-4 border-2 border-primary-foreground border-t-transparent rounded-full animate-spin" />
                    Salvataggio...
                  </>
                ) : saveStatus === "saved" ? (
                  <>
                    <CheckCircle className="h-4 w-4" />
                    Salvato
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4" />
                    Salva
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-8">
        {isLoadingData ? (
          <div className="space-y-6">
            <div className="animate-pulse">
              <div className="h-4 bg-muted rounded w-1/4 mb-2" />
              <div className="h-10 bg-muted rounded-xl" />
            </div>
            <div className="animate-pulse">
              <div className="h-4 bg-muted rounded w-1/4 mb-2" />
              <div className="h-24 bg-muted rounded-xl" />
            </div>
          </div>
        ) : (
          <div className="space-y-6">
            <Section icon={<AlertTriangle />} title="Informazioni generali">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Titolo *
                  </label>
                  <input
                    type="text"
                    value={form.titolo}
                    onChange={(e) => set("titolo", e.target.value)}
                    placeholder="Breve descrizione del problema"
                    className="w-full px-4 py-3 rounded-xl border border-border bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 transition-all"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Descrizione dettagliata
                  </label>
                  <textarea
                    value={form.descrizione}
                    onChange={(e) => set("descrizione", e.target.value)}
                    placeholder="Descrivi il problema in dettaglio"
                    rows={4}
                    className="w-full px-4 py-3 rounded-xl border border-border bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 transition-all resize-none"
                  />
                </div>
              </div>
            </Section>

            <Section icon={<Tag />} title="Categoria e Priorità">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Tipo
                  </label>
                  <select
                    value={form.tipo}
                    onChange={(e) => set("tipo", e.target.value)}
                    className="w-full px-4 py-3 rounded-xl border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 transition-all appearance-none"
                  >
                    {TIPI.map(tipo => (
                      <option key={tipo} value={tipo}>{tipo}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Priorità
                  </label>
                  <select
                    value={form.priorita}
                    onChange={(e) => set("priorita", e.target.value)}
                    className="w-full px-4 py-3 rounded-xl border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 transition-all appearance-none"
                  >
                    {PRIORITA.map(priorita => (
                      <option key={priorita} value={priorita}>
                        {priorita.charAt(0).toUpperCase() + priorita.slice(1)}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </Section>

            <Section icon={<Building2 />} title="Riferimenti">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Struttura
                  </label>
                  <select
                    value={form.struttura_id}
                    onChange={(e) => set("struttura_id", e.target.value)}
                    className="w-full px-4 py-3 rounded-xl border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 transition-all appearance-none"
                  >
                    <option value="">Seleziona una struttura</option>
                    {strutture.map(struttura => (
                      <option key={struttura.id} value={struttura.id}>
                        {struttura.nome}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Segnalato da
                  </label>
                  <input
                    type="text"
                    value={form.segnalato_da}
                    onChange={(e) => set("segnalato_da", e.target.value)}
                    placeholder="Nome di chi ha segnalato"
                    className="w-full px-4 py-3 rounded-xl border border-border bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 transition-all"
                  />
                </div>
              </div>
            </Section>

            {/* Error */}
            {saveError && (
              <div className="flex items-center gap-2 p-4 rounded-xl bg-destructive/10 border border-destructive/20">
                <AlertTriangle className="h-4 w-4 text-destructive" />
                <span className="text-sm text-destructive">{saveError}</span>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
